import { z } from "zod";
import type { TLShape } from "@tldraw/tlschema";
import type { OperationContext } from "./agent-operation-context";
export type VariantVote = { name: string; selected: boolean };
export const variantOperation = z
  .object({
    type: z.literal("set_variant_review"),
    id: z.string().regex(/^shape:[\w-]{1,100}$/),
    enabled: z.boolean().optional(),
    selected: z.boolean().optional(),
    status: z
      .enum(["undecided", "shortlisted", "selected", "rejected"])
      .optional(),
    vote: z.boolean().nullable().optional(),
  })
  .strict();
const statuses = ["undecided", "shortlisted", "selected", "rejected"];
export function variantVotes(
  meta: Record<string, unknown>,
): Record<string, VariantVote> {
  const value = meta.variantVotes;
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, v]) =>
        v &&
        typeof v === "object" &&
        typeof v.name === "string" &&
        typeof v.selected === "boolean",
    ),
  );
}
export function validateVariantReview(meta: Record<string, unknown>) {
  if (
    meta.variantReviewEnabled !== undefined &&
    typeof meta.variantReviewEnabled !== "boolean"
  )
    throw new Error("Invalid variant review control");
  if (
    meta.variantSelected !== undefined &&
    typeof meta.variantSelected !== "boolean"
  )
    throw new Error("Invalid variant selection");
  if (
    meta.variantStatus !== undefined &&
    !statuses.includes(String(meta.variantStatus))
  )
    throw new Error("Invalid variant status");
  if (meta.variantVotes !== undefined) {
    const votes = variantVotes(meta);
    if (
      !meta.variantVotes ||
      typeof meta.variantVotes !== "object" ||
      Array.isArray(meta.variantVotes) ||
      Object.keys(meta.variantVotes).length > 100 ||
      Object.keys(votes).length !== Object.keys(meta.variantVotes).length ||
      Object.entries(votes).some(
        ([id, v]) => id.length > 150 || v.name.length > 200,
      )
    )
      throw new Error("Invalid variant votes");
  }
}
export function canEditVariantVotes(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  user: { id: string; name: string },
) {
  const a = variantVotes(before),
    b = variantVotes(after);
  for (const id of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (id !== user.id && JSON.stringify(a[id]) !== JSON.stringify(b[id]))
      return false;
    if (
      id === user.id &&
      b[id] &&
      JSON.stringify(a[id]) !== JSON.stringify(b[id]) &&
      b[id].name !== user.name
    )
      return false;
  }
  return true;
}
export function voteFor(
  meta: Record<string, unknown>,
  user: { id: string; name: string },
  selected: boolean | null,
) {
  const votes = { ...variantVotes(meta) };
  if (selected === null) delete votes[user.id];
  else votes[user.id] = { name: user.name, selected };
  return votes;
}
export function variantReview(shape: TLShape) {
  return {
    id: shape.id,
    parentId: shape.parentId,
    assetId: shape.type === "image" ? shape.props.assetId : undefined,
    enabled: shape.meta.variantReviewEnabled === true,
    selected: shape.meta.variantSelected === true,
    status: String(shape.meta.variantStatus ?? "undecided"),
    votes: variantVotes(shape.meta),
  };
}
export function applyVariantOperation(input: unknown, c: OperationContext) {
  const parsed = variantOperation.safeParse(input);
  if (!parsed.success) return false;
  const op = parsed.data,
    shape = c.editable(op.id);
  if (shape.type !== "image")
    throw new Error("Variant reviews attach to image objects");
  const meta = { ...shape.meta };
  if (op.enabled !== undefined) meta.variantReviewEnabled = op.enabled;
  if (op.selected !== undefined) {
    meta.variantSelected = op.selected;
    meta.variantStatus = op.selected ? "selected" : "undecided";
  }
  if (op.status !== undefined) {
    meta.variantStatus = op.status;
    meta.variantSelected = op.status === "selected";
  }
  if (op.vote !== undefined)
    meta.variantVotes = voteFor(
      meta,
      { id: c.userId, name: c.userName ?? c.userId },
      op.vote,
    );
  c.set({ ...shape, meta });
  return true;
}
